import { TelefonePipe } from '../../../shared/pipes/telefone.pipe';
import { CanalSicPublicoResponse, EnderecoCanalSicPublico } from '../models/canal-sic-publico.model';
import { PrazoAtendimento } from '../models/prazo-atendimento.model';

const telefonePipe = new TelefonePipe();

/**
 * O que a página de informações do e-SIC e o atendimento presencial exibem do canal em destaque,
 * escrito **num lugar só** para que as duas telas digam a mesma coisa.
 *
 * ⚠️ Nenhum dado do e-SIC é escrito aqui: os três prazos vêm **prontos** da leitura pública do
 * canal (`GET sic/publico/canal`), na unidade do canal. Não há texto da Lei nº 12.527/2011 como
 * reserva — um órgão que não permite prorrogação lê *"Não prevista"*, e não o que a lei permitiria.
 */

/** O texto da linha *Responsável* quando o backend devolve o nome nulo — o mesmo nas duas telas. */
export const RESPONSAVEL_EM_ATUALIZACAO = 'em atualização';

/** O texto do estado vazio das duas telas: falha, `429` e não publicado caem aqui. */
export const CANAL_SIC_NAO_PUBLICADO = 'O atendimento do e-SIC ainda não foi publicado por este órgão.';

/** Os três prazos do canal: rótulo e ícone são da tela; o **valor** é o do backend. */
export function prazosSic(canal: CanalSicPublicoResponse | null): readonly PrazoAtendimento[] {
  if (!canal) return [];

  const prazos: (PrazoAtendimento | null)[] = [
    prazo('resposta', 'Prazo padrão de resposta', 'timer', canal.prazoRespostaDescricao),
    prazo('prorrogacao', 'Prorrogação de prazo', 'hourglass', canal.prazoProrrogacaoDescricao),
    prazo('recurso', 'Prazo de recurso', 'scale', canal.prazoRecursoDescricao),
  ];

  return prazos.filter((item): item is PrazoAtendimento => item !== null);
}

/** O nome do responsável, ou *"em atualização"* — a linha é exibida sempre. */
export function responsavelSic(nome: string | null | undefined): string {
  return nome?.trim() || RESPONSAVEL_EM_ATUALIZACAO;
}

/**
 * O telefone para leitura: `(71) 3000-5000`. O admin grava **só os dígitos** (o `ngx-mask` do
 * `sai-adm-web` tira a máscara do valor, como na Ouvidoria), então quem formata é a tela. O link de
 * chamada continua usando só os dígitos.
 */
export function telefoneSic(telefone: string | null | undefined): string {
  return telefonePipe.transform(limpo(telefone));
}

/**
 * O endereço para leitura: `Rua X, 285, Térreo — Centro, Rio Branco-AC, CEP: 69900-000`. Parte
 * vazia some junto com o separador dela.
 */
export function enderecoSic(endereco: EnderecoCanalSicPublico | null | undefined): string {
  if (!endereco) return '';

  const rua = juntar(', ', endereco.logradouro, endereco.numero, endereco.complemento);
  const cidade = juntar('-', endereco.cidade, endereco.uf);
  const local = juntar(', ', endereco.bairro, cidade);
  const cep = limpo(endereco.cep) ? `CEP: ${cepSic(endereco.cep)}` : '';

  return juntar(', ', juntar(' — ', rua, local), cep);
}

/**
 * O endereço como consulta de mapa e de rota: sem complemento (que confunde a busca) e sem
 * rótulo — `Rua X, 285, Centro, Rio Branco - AC, 69900-000`.
 */
export function consultaEnderecoSic(endereco: EnderecoCanalSicPublico | null | undefined): string {
  if (!endereco) return '';

  return juntar(
    ', ',
    endereco.logradouro,
    endereco.numero,
    endereco.bairro,
    juntar(' - ', endereco.cidade, endereco.uf),
    endereco.cep
  );
}

/** O CEP com hífen quando vier só com os 8 dígitos (o admin pode gravá-lo sem máscara). */
function cepSic(cep: string | null | undefined): string {
  const digitos = limpo(cep).replace(/\D/g, '');

  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : limpo(cep);
}

function prazo(id: string, rotulo: string, icone: string, valor: string | null): PrazoAtendimento | null {
  return limpo(valor) ? { id, rotulo, icone, valor: limpo(valor) } : null;
}

function limpo(valor: string | null | undefined): string {
  return (valor ?? '').trim();
}

function juntar(separador: string, ...partes: (string | null | undefined)[]): string {
  return partes.map(limpo).filter(Boolean).join(separador);
}
